import React, { useMemo, useState, useEffect } from 'react';
import { getImageSrcAndSrcSet } from '../services/getUrlFromCdn';
import ImageSlider from './ImageSlider';
import clsx from 'clsx';
import useCheckoutState, { checkoutLayouts } from '../hooks/useCheckoutState';

const Media = React.memo(
  ({ media }) => {
    const hasMedia = media && media.length > 0;
    const { checkoutLayout, isPreview, checkoutType } = useCheckoutState();
    const [isInitialRender, setIsInitialRender] = useState(true);

    useEffect(() => {
      // Remove the aspect ratio after initial render
      const timer = setTimeout(() => {
        setIsInitialRender(false);
      }, 100); // Small delay to ensure smooth transition

      return () => clearTimeout(timer);
    }, []);

    if (!hasMedia) {
      return null;
    }

    const containerMaxWidth = useMemo(() => {
      switch (checkoutLayout) {
        case checkoutLayouts.DOUBLE:
        case checkoutLayouts.SPLIT:
          return 448;
        default:
          return 480;
      }
    }, [checkoutLayout]);

    const mediaSizesCalculated = useMemo(
      () =>
        media.map(m => {
          // Now the image object has the file data loaded
          const maxWidth = parseInt(m.width > containerMaxWidth ? containerMaxWidth : m.width);

          // Calculate the aspect ratio and the resulting height
          const aspectRatio = m.width / m.height;
          const newHeight = parseInt(maxWidth / aspectRatio);

          const image = getImageSrcAndSrcSet({
            url: m.url,
            width: maxWidth,
            height: newHeight,
            isPreview,
          });

          return {
            ...m,
            width: maxWidth,
            height: newHeight,
            src: image.src,
            srcSet: image.srcSet,
            aspectRatio,
          };
        }),
      [media, containerMaxWidth, isPreview],
    );

    const imageStyle = useMemo(() => {
      return {
        maxWidth: '100%',
        width: '100%',
        height: 'auto',
        ...(isInitialRender && {
          aspectRatio: `${mediaSizesCalculated[0].aspectRatio}`,
        }),
      };
    }, [mediaSizesCalculated, isInitialRender]);

    if (mediaSizesCalculated.length === 1) {
      return (
        <img
          src={mediaSizesCalculated[0].src}
          srcSet={mediaSizesCalculated[0].srcSet}
          alt={media[0].name}
          width={mediaSizesCalculated[0].width}
          height={mediaSizesCalculated[0].height}
          style={imageStyle}
          className={clsx('cp_product-image object-cover object-center color-transparent', {
            rounded:
              checkoutLayout === checkoutLayouts.DOUBLE || checkoutLayout === checkoutLayouts.SPLIT,
            'rounded-t': checkoutType !== 'embed',
          })}
          loading="eager"
        />
      );
    }

    return (
      <ImageSlider.Carousel count={mediaSizesCalculated.length}>
        {mediaSizesCalculated.map((media, index) => (
          <ImageSlider.Slide key={media._id}>
            <img
              src={media.src}
              srcSet={media.srcSet}
              alt={media.title}
              width={media.width}
              height={media.height}
              style={imageStyle}
              className={clsx('cp_product-image object-cover object-center color-transparent', {
                rounded:
                  checkoutLayout === checkoutLayouts.DOUBLE ||
                  checkoutLayout === checkoutLayouts.SPLIT,
                'rounded-t': checkoutType !== 'embed',
              })}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </ImageSlider.Slide>
        ))}
      </ImageSlider.Carousel>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.media === nextProps.media && prevProps.checkoutType === nextProps.checkoutType;
  },
);

export default Media;
